import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '@/lib/api/client';
import { REFRESH_LEAD_MS } from '@/lib/config';
import {
  clearSession,
  getClaims,
  hasSessionMarker,
  isAuthenticated,
  setTokens,
  timeUntilExpiry,
} from './session';
import { isCredentialRefusal, refreshSession, setRefreshFailureHandler } from '@/lib/api/refresh';
import { roleHas, type Permission } from '@/lib/rbac/permissions';
import i18n, { applyChromeLocale } from '@/lib/i18n/config';
import { useToast } from '@/lib/ui/Toast';
import type { AuthTokens, Role, User } from '@/lib/api/types';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  role: Role | null;
  can: (permission: Permission) => boolean;
  /** True for translator-style accounts: can translate but not write (spec §6.3). */
  isTranslateOnly: boolean;
  setSession: (tokens: AuthTokens) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /**
   * True while the cold-boot refresh is being retried because the server could not answer
   * (as opposed to refusing). Drives the copy on the boot splash, so a slow or rate-limited
   * backend does not look like a silent hang.
   */
  reconnecting: boolean;
  /** Deep-link to return to after re-auth (spec §3.2). */
  pendingDeepLink: string | null;
  setPendingDeepLink: (path: string | null) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Backoff for a cold boot the server could not answer. Totals ~92 s, which outlasts a
 * 60-second rate-limit window, so a console reloaded into a rate limit recovers by itself
 * instead of showing a sign-in screen to someone who is signed in.
 */
const BOOT_RETRY_MS = [1_000, 3_000, 8_000, 20_000, 30_000, 30_000];

/**
 * When a proactive (scheduled) refresh fails transiently, try again this much later. The
 * schedule leads expiry by REFRESH_LEAD_MS, so there is time for a few attempts before the
 * access token lapses — and if it does lapse, the next request's 401 refreshes on demand.
 */
const SCHEDULED_RETRY_MS = 15_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [reconnecting, setReconnecting] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [pendingDeepLink, setPendingDeepLink] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hardLogout = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    clearSession();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // The toast API object changes identity with its provider's state; read it through a
  // ref so the refresh scheduler stays stable and never re-arms on a toast.
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);
  const warnedRef = useRef(false);

  // Proactive refresh ~60s before expiry; reschedules itself (plan §4.2).
  const scheduleRefresh = useCallback(
    (delayOverride?: number) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!isAuthenticated()) return;
      const delay = delayOverride ?? Math.max(0, timeUntilExpiry() - REFRESH_LEAD_MS);
      timerRef.current = setTimeout(() => {
        void refreshSession()
          .then(() => {
            warnedRef.current = false;
            scheduleRefresh();
          })
          .catch((err: unknown) => {
            // A credential refusal already hard-logged out via the failure handler.
            if (isCredentialRefusal(err)) return;
            // Anything else (429, 5xx, offline) keeps the session: say so once per
            // streak, and try again later rather than in a tight loop.
            if (!warnedRef.current) {
              warnedRef.current = true;
              toastRef.current.warning(i18n.t('common.sessionRenewDelayed'), i18n.t('common.sessionRenewDelayedBody'));
            }
            scheduleRefresh(SCHEDULED_RETRY_MS);
          });
      }, delay);
    },
    [],
  );

  const refreshUser = useCallback(async () => {
    const me = await api.get<User>('/admin/me');
    setUser(me);
    // The profile carries the editor's interface language and is the source of truth for
    // it. Applied here because every path that establishes a session comes through here:
    // cold boot, sign-in, and the account page's own save.
    if (me.locale === 'en' || me.locale === 'ar') applyChromeLocale(me.locale);
  }, []);

  const setSession = useCallback(
    async (tokens: AuthTokens) => {
      setTokens(tokens);
      await refreshUser();
      setStatus('authenticated');
      scheduleRefresh();
    },
    [refreshUser, scheduleRefresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/admin/auth/logout');
    } catch {
      /* logout is best-effort; always clear locally */
    }
    hardLogout();
  }, [hardLogout]);

  // Wire the refresh-failure handler once.
  useEffect(() => {
    setRefreshFailureHandler(hardLogout);
  }, [hardLogout]);

  // Bootstrap on mount: attempt a cookie-based refresh to re-establish a session after a
  // hard reload — unless this browser has never signed in (see `hasSessionMarker`), in
  // which case there is nothing to re-establish and the rate-limited call is skipped.
  useEffect(() => {
    let cancelled = false;
    if (!hasSessionMarker()) {
      setStatus('unauthenticated');
      return;
    }
    (async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          await refreshSession();
          if (cancelled) return;
          await refreshUser();
          if (cancelled) return;
          setReconnecting(false);
          setStatus('authenticated');
          scheduleRefresh();
          return;
        } catch (err) {
          if (cancelled) return;
          // A refusal — the credential is dead, or was never there. This is the ordinary
          // "you are signed out" path and it must stay instant.
          if (isCredentialRefusal(err) || attempt >= BOOT_RETRY_MS.length) {
            setReconnecting(false);
            setStatus('unauthenticated');
            return;
          }
          // The server could not answer right now (a 429 from the per-IP auth limit, a
          // restart, a flaky link). Bouncing to sign-in would be a lie about the session
          // and a trap: re-entering credentials spends another request against the same
          // limit. Hold the splash, say why, and try again.
          setReconnecting(true);
          await new Promise((resolve) => setTimeout(resolve, BOOT_RETRY_MS[attempt]));
          if (cancelled) return;
        }
      }
    })();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [refreshUser, scheduleRefresh]);

  const role = user?.role ?? getClaims()?.role ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      role,
      can: (p: Permission) => roleHas(role, p),
      isTranslateOnly: roleHas(role, 'content:translate') && !roleHas(role, 'content:write'),
      setSession,
      logout,
      refreshUser,
      reconnecting,
      pendingDeepLink,
      setPendingDeepLink,
    }),
    [status, user, role, reconnecting, setSession, logout, refreshUser, pendingDeepLink],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
